import React from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { AlertPalette } from '../../constants/alertTheme';

/**
 * THE SHARED CHROME FOR BOTH LIVE ALERTS (owner, 2026-09-09).
 *
 * The landmine and competitor cards had grown apart — one Tailwind, one a
 * 485-line stylesheet, with different headers, different dismiss buttons and
 * different ideas of where the trigger quote goes. They appear in the SAME
 * corner of the SAME screen, seconds apart, so a rep was learning two layouts
 * to read one kind of information.
 *
 * One shell, two palettes. What differs between them is colour and content;
 * everything structural is here, which is also what makes the macOS and WPF
 * ports a translation rather than a redesign.
 *
 * READING ORDER IS THE DESIGN. Badge (what kind, in peripheral vision) ->
 * name (which landmine / which competitor, specifically) -> speaker (whose
 * words, because your own slip and the customer's remark need opposite
 * reactions) -> the quote (what actually fired it) -> what to do. Nothing
 * else is on the card.
 */
export interface AlertShellProps {
  palette: AlertPalette;
  badgeLabel: string;
  /** The landmine's configured trigger phrase, or the competitor's name —
   *  distinct from `trigger`, which is the transcript quote that fired it. */
  name?: string;
  /** "You said" / "Customer said" — already resolved by `speakerLabel`. Every
   *  live alert has one; only a Knowledge Search result, which nobody said,
   *  leaves it out (no speaker line). */
  speaker?: string;
  /** The transcript text that triggered the alert. */
  trigger?: string;
  /** Omitted in the Landmines accordion, where every landmine is kept for the
   *  call; the close button is not rendered without it. */
  onDismiss?: () => void;
  /** The thumbs up/down control, kept because it feeds detector quality. */
  feedback?: React.ReactNode;
  /**
   * Makes the card its own accordion (the Landmines list): the header line
   * toggles, a chevron sits at its far right, and while collapsed only the
   * header line shows. Absent, the card renders exactly as it always has.
   */
  onToggleCollapse?: () => void;
  /** Only read when `onToggleCollapse` is set. */
  isCollapsed?: boolean;
  /** The card's outer spacing. The live stacks keep the default; Knowledge
   *  Search spaces its cards itself. */
  spacingClassName?: string;
  children: React.ReactNode;
}

/**
 * Badge -> name -> speaker: the part of the header that identifies the alert.
 * `isSingleLine` (a collapsed accordion card only) keeps the header to one
 * line: the name ellipsizes and the speaker never wraps. Every other card
 * keeps its original wrapping.
 */
const HeaderIdentity: React.FC<
  Pick<AlertShellProps, 'palette' | 'badgeLabel' | 'name' | 'speaker'> & { isSingleLine: boolean }
> = ({ palette, badgeLabel, name, speaker, isSingleLine }) => (
  <>
    <span
      className="rounded px-1.5 py-0.5 text-[10px] font-bold tracking-[0.08em]"
      style={{ background: palette.badge, color: palette.badgeText }}
    >
      {badgeLabel}
    </span>
    {name && (
      <span
        className={`text-[12px] font-semibold${isSingleLine ? ' min-w-0 truncate' : ''}`}
        style={{ color: palette.heading }}
      >
        {name}
      </span>
    )}
    {speaker && (
      <span
        className={`text-[11px] font-medium${isSingleLine ? ' flex-shrink-0 whitespace-nowrap' : ''}`}
        style={{ color: palette.muted }}
      >
        {speaker}
      </span>
    )}
  </>
);

export const AlertShell: React.FC<AlertShellProps> = ({
  palette,
  badgeLabel,
  name,
  speaker,
  trigger,
  onDismiss,
  feedback,
  onToggleCollapse,
  isCollapsed = false,
  spacingClassName = 'mb-3',
  children,
}) => {
  const isCollapsible = Boolean(onToggleCollapse);
  const isBodyHidden = isCollapsible && isCollapsed;
  const identity = (
    <HeaderIdentity
      palette={palette}
      badgeLabel={badgeLabel}
      name={name}
      speaker={speaker}
      isSingleLine={isBodyHidden}
    />
  );
  const Chevron = isCollapsed ? ChevronRight : ChevronDown;

  return (
    <div
      className={`${spacingClassName} overflow-hidden rounded-lg`}
      style={{
        background: palette.surface,
        border: `1px solid ${palette.border}`,
        // The spine. One rule carries the whole colour identity of the card, so
        // the fill can stay faint enough to read body text on.
        borderLeft: `3px solid ${palette.accent}`,
      }}
    >
      {/*
        Collapsible: a click anywhere on the header line toggles (it bubbles
        here), except inside the feedback control, which stops it. The
        identity is a real <button> so the card is reachable and operable
        from the keyboard; the feedback control sits beside it, not inside,
        because a button cannot contain a button.
      */}
      <div
        className={`flex items-center gap-2 px-3 pt-2.5 pb-2 ${isCollapsible ? 'cursor-pointer' : ''}`}
        onClick={onToggleCollapse}
      >
        {isCollapsible ? (
          <button
            type="button"
            aria-expanded={!isCollapsed}
            className="flex min-w-0 items-center gap-2 rounded text-left"
          >
            {identity}
          </button>
        ) : (
          identity
        )}
        <span className="ml-auto flex items-center gap-1">
          {feedback && (
            <span className="flex items-center" onClick={(event) => event.stopPropagation()}>
              {feedback}
            </span>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              aria-label="Dismiss alert"
              className="rounded p-0.5 transition-opacity hover:opacity-60"
              style={{ color: palette.muted }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isCollapsible && (
            <Chevron aria-hidden="true" className="h-4 w-4 flex-shrink-0" style={{ color: palette.muted }} />
          )}
        </span>
      </div>

      {!isBodyHidden && trigger && (
        <div className="px-3 pb-2.5">
          <h4
            className="mb-1 text-[10px] font-bold uppercase tracking-[0.07em]"
            style={{ color: palette.heading }}
          >
            Trigger
          </h4>
          <p
            className="text-[12px] italic leading-snug"
            style={{ color: palette.muted }}
          >
            &ldquo;{trigger}&rdquo;
          </p>
        </div>
      )}

      {!isBodyHidden && <div style={{ borderTop: `1px solid ${palette.divider}` }}>{children}</div>}
    </div>
  );
};

/**
 * One labelled block. The rule above each section is what divides the card
 * without boxing every group in its own tinted panel, which is what made the
 * old cards look busy at a glance.
 */
export const AlertSection: React.FC<{
  palette: AlertPalette;
  title: string;
  children: React.ReactNode;
}> = ({ palette, title, children }) => (
  <div
    className="px-3 py-2.5"
    style={{ borderBottom: `1px solid ${palette.divider}` }}
  >
    <h4
      className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.07em]"
      style={{ color: palette.heading }}
    >
      {title}
    </h4>
    {children}
  </div>
);

/** A section's paragraph: the landmine's quick tips, the competitor's talking points. */
export const AlertParagraph: React.FC<{
  palette: AlertPalette;
  children: React.ReactNode;
}> = ({ palette, children }) => (
  <p className="text-[13px] leading-relaxed" style={{ color: palette.body }}>
    {children}
  </p>
);

/**
 * Bullets. `marker="cross"` is the only variant, for "you cannot say" — the one
 * list where mistaking it for its neighbour is the expensive error, so it does
 * not rely on the section heading alone to be told apart.
 */
export const AlertBulletList: React.FC<{
  palette: AlertPalette;
  items: string[];
  marker?: 'dot' | 'cross';
}> = ({ palette, items, marker = 'dot' }) => (
  <ul className="space-y-1">
    {items.map((item, index) => (
      <li key={index} className="flex items-start gap-1.5">
        <span
          className="mt-[2px] flex-none text-[11px] leading-tight"
          style={{ color: palette.accent }}
          aria-hidden="true"
        >
          {marker === 'cross' ? '✕' : '•'}
        </span>
        <span className="text-[13px] leading-relaxed" style={{ color: palette.body }}>
          {item}
        </span>
      </li>
    ))}
  </ul>
);
